/**
 * The re-anchor unit of an entity switch (DV-3421): does the target need the
 * token moved first, and moving it. Split out of `entity-scope.ts`, which owns
 * the sequence and the commit; nothing here writes scope.
 */
import { entityClaim, scopeRootOfToken, tokenSubject } from './entity-claim';
import {
  fetchEntityInfoRequest,
  fetchScopeCheckRequest,
  setUserContextRequest,
} from './entity-scope-api';
import { endSession, refreshTokenFresh } from './auth-token';
import { SessionEndingError, SwitchEntityError } from './switch-errors';
import { getEntityScope } from './entity-scope-read';
import type { SwitchEntityTarget } from './entity-scope';
import type { EntityCheckoutInfo } from '../types/org-selection.types';
import { inTokenTree } from './entity-tree';

/**
 * Adopts a fresh token when the session's token names no account (no scope
 * root). The one statement of that rule, for `switchEntity` and the
 * post-registration account reads (DV-3745).
 *
 * <p>A new registrant's token was minted before the registration wrote their
 * account into their Keycloak attributes, so it names none; one minted now
 * names the account once it exists. Best effort, never rejects: a failed
 * refresh leaves the caller where it was, and a token whose refresh stays
 * rootless pays one refresh and nothing else. No session, no refresh.
 */
export async function refreshIfRootless(): Promise<void> {
  if (scopeRootOfToken() !== null || tokenSubject() === null) return;
  await refreshTokenFresh().catch(() => undefined);
}

/** Whether the target is inside what the token may currently reach. */
export type Verdict = 'in-scope' | 'out-of-scope';

/**
 * Does this switch need the token re-anchored before it can work?
 *
 * <p>Asks the backend rather than comparing ids locally (DV-3421 review
 * findings 3 and 5). Two local shortcuts were wrong:
 *
 * <ul>
 *   <li>`parentEntityId` is NOT the tree root. `useOrgSelection` passes
 *       `parentEntityId ?? entityId`, so for a member whose own account is a
 *       sub-entity it is that sub-entity's id. Comparing it against the
 *       token's root made in-tree switches look cross-tree, costing a
 *       needless Keycloak write and refresh — and turning a purely local
 *       switch into one that can fail or end the session.
 *   <li>Trees deeper than one level would break the same way, since no
 *       caller walks to the true root.
 * </ul>
 *
 * <p>`/entities/scope/check` answers with the guard's own rule — whether the
 * guard would honour `X-Entity-UUID` for the target, asked in advance. That
 * includes unrestricted callers: an internal admin's token is anchored to
 * their own account, so every customer account is outside its subtree, yet
 * the guard grants them any existing account, and the backend says so from
 * `guard.unrestricted-roles` itself. Their switches therefore stay free, as
 * DV-3573 made them, with no copy of that list in the UI (review rounds 2–4).
 *
 * <p>A lookup error is not answered here; it propagates and the switch stops
 * at `'scope-check'` (review round 2, issue 5) — see the call site.
 */
export async function reanchorVerdict(target: SwitchEntityTarget): Promise<Verdict> {
  // No readable token means nothing will authorise anyway; re-anchoring an
  // absent session would only fail louder.
  if (tokenSubject() === null) return 'in-scope';
  if (reachableWithoutAsking(target.entityId)) return 'in-scope';
  return (await fetchScopeCheckRequest(target.entityId)) ? 'in-scope' : 'out-of-scope';
}

/**
 * Is the target provably inside the token's reach from what this tab already
 * holds? Spares the common in-tree switch the `/entities/scope/check` round trip
 * (review rounds 2–4, item 10) — the DV-3573 fast path was a switch with no
 * round trip before the entity-info fetch.
 *
 * <p>Only ever answers yes, by `inTokenTree`'s proofs. The header tree — the
 * switch surface people use most — only offers accounts from the cached tree.
 * Anything else, including every cross-tree candidate, asks the backend.
 */
const reachableWithoutAsking = (entityId: string): boolean => {
  const root = scopeRootOfToken();
  return root !== null && inTokenTree(getEntityScope(), root, entityId);
};

/**
 * Move the token to the target's tree, then fetch the target's document under
 * it. Runs inside the switch's `beginSwitch` window; the caller ends it.
 *
 * <p>DV-3573 removed `POST /users/context` from every switch on the grounds
 * that the token no longer needs to follow the selection. That holds only
 * while the selection stays inside the token's allowed set. The guard derives
 * that set from the token's root subtree, so a selection outside it is refused
 * on EVERY request — permanently, because nothing moves the token any more.
 * Measured in QA on 13 Oct: a user added to a second company got 44 DENIED
 * decisions across four services and a silently empty app, because their own
 * first company was no longer reachable.
 *
 * <p>Widening the guard instead is not an option: `EntityScopeResolver` caches
 * the allowed set keyed by ENTITY, shared by every user of that account, so
 * folding one user's extra memberships into it would hand those memberships to
 * everyone else in the account.
 *
 * <p>After the context write the session must never be left half-moved —
 * attributes on the target while the token cannot reach it. Evidence, in the
 * order it is read:
 *
 * <ul>
 *   <li>The write's own answer. A 4xx is a refusal: nothing was written, so
 *       the switch fails at once with no refresh (review of 24a2f326, item 8).
 *       A 5xx, timeout or network error is ambiguous — user-service may have
 *       written before the gateway gave up — and is settled below.
 *   <li>The fresh token. Unmintable → end the session (spec D9): the
 *       attributes may sit on the target and nothing can reach them. Its
 *       `EntityUUID` is the write's signature (user-service sets it to the
 *       target), so after an ambiguous write a token still on another entity
 *       is a clean failure, and one on the target means the write landed
 *       (review of 24a2f326, item 4).
 *   <li>`/entities/scope/check` under that token: will the guard honour the
 *       target? No → half-moved → end the session. If the check itself fails,
 *       a token on the target whose root also moved is evidence enough to
 *       commit (review of 24a2f326, item 3); without it the switch fails and
 *       nothing is ended.
 * </ul>
 *
 * <p>What "half-moved" looks like in practice (review of 24a2f326, item 7):
 * user-service writes `RootEntityUUID` only when its root lookup answers, and
 * that lookup gives up after 15 s. A cross-tree switch that hits the timeout
 * moves `EntityUUID` but leaves the OLD root, so under `honour-selection` the
 * target is outside the token's tree and the check says no. Ending the session
 * does not repair the attribute. The next login does, if anything: `CallBack`
 * replays `/users/context` for the token's `EntityUUID` (the target), which
 * runs the root lookup again — and a refresh after a successful replay lands
 * on the target's tree. If that lookup fails too, the user lands with the
 * stale root and the scope guard refuses the target until a later context
 * write succeeds. With `honour-selection` off the root does not matter — only
 * the token's own entity is honoured — so the same timeout is harmless there.
 *
 * <p>Two outcomes are NOT half-moved and end nothing (review of 0a17c730,
 * item 1): a different user taking the token over mid-switch — their session,
 * not ours to end — and a same-user token that sits in a third tree because
 * another tab re-anchored the attributes again. Both are coherent; the switch
 * fails with {@link SwitchEntityError} and this tab's reconciler takes over.
 */
export async function reanchor(
  target: SwitchEntityTarget,
  groupName: string,
  userId: string,
  rootBefore: string,
): Promise<{ entityInfo: EntityCheckoutInfo[]; root: string | null }> {
  // Sent with NO selection header (review round 5, item 3 — the endpoint's
  // `tokenOnly` flag): it acts on the caller's token, and the tab's selection
  // may already be one that token cannot reach.
  let ambiguousWrite: { cause: unknown } | null = null;
  try {
    await setUserContextRequest(groupName, userId, target.entityId);
  } catch (cause) {
    if (isRefusal(cause)) throw new SwitchEntityError('set-context', cause);
    ambiguousWrite = { cause };
  }

  // FRESH, never a join onto a refresh already in flight — one begun before
  // the write above mints its claims from the old attributes, and the switch
  // would "succeed" still anchored to the previous company.
  try {
    await refreshTokenFresh();
  } catch (cause) {
    endSession();
    throw new SessionEndingError(cause);
  }

  if (tokenSubject() !== userId) throw changedHands();
  // Read BEFORE anything else awaits (review of 24a2f326, item 1): this is the
  // token the write produced, and what the check below is asked about.
  const rootAfter = scopeRootOfToken();
  const landed = entityClaim() === target.entityId;
  if (ambiguousWrite && !landed) throw new SwitchEntityError('set-context', ambiguousWrite.cause);

  let reachable: boolean;
  try {
    reachable = await fetchScopeCheckRequest(target.entityId);
  } catch (cause) {
    if (!(landed && rootAfter !== rootBefore)) throw new SwitchEntityError('set-context', cause);
    reachable = true;
  }
  if (!reachable) {
    endSession();
    throw new SessionEndingError(
      new Error('context write did not make the target reachable under the new token'),
    );
  }
  // The check awaited. Its answer is about `rootAfter`; if another tab has
  // re-anchored since, it is not about the token we would stamp — the same
  // question as after the read below, settled the same way.
  await confirmStillMoved(userId, rootAfter);

  // From here requests carry the moved token. Any OTHER request this tab
  // makes before the commit still asserts the OLD selection and is refused
  // (review of b5251a34, item 2 — sending the target let a create from the
  // still-mounted old page land in the target account). This read itself
  // sends none (`getEntityInfoForSwitch` is `tokenOnly`).
  //
  // A failure here does NOT fail the switch. The token has moved; failing
  // would leave the tab on a selection the token can no longer reach, or end a
  // session over a document read. Carry on with no document instead, which is
  // the empty-result path below: require checkout, no watermark — the same
  // fail-closed defaults an entity with no document has always had (BC-5).
  let entityInfo: EntityCheckoutInfo[];
  try {
    entityInfo = await fetchEntityInfoRequest(target.entityId);
  } catch (cause) {
    console.warn('switchEntity: entity info unavailable after re-anchor; continuing with defaults', cause);
    entityInfo = [];
  }

  await confirmStillMoved(userId, rootAfter);
  return { entityInfo, root: rootAfter };
}

/**
 * An await since the move; is the stored token still this user's, anchored
 * where the move put it? Asked after the confirming check and after the
 * entity read (reviews of 0a17c730 item 1, 24a2f326 item 1).
 *
 * <ul>
 *   <li>A different user: throw, end nothing (see `reanchor`).
 *   <li>Same user, another root: one fresh refresh, which mints from the
 *       attributes as they are now. Back at `movedTo` — a late write the token
 *       writer let through (equal issue second) — carry on. Still elsewhere —
 *       another tab re-anchored the attributes — throw, end nothing: the
 *       session is coherent, just not on our target.
 *   <li>The refresh fails: the attributes point at a target no token can be
 *       minted for — end the session, as `reanchor` does.
 * </ul>
 */
async function confirmStillMoved(userId: string, movedTo: string | null): Promise<void> {
  if (tokenSubject() !== userId) throw changedHands();
  if (scopeRootOfToken() === movedTo) return;

  try {
    await refreshTokenFresh();
  } catch (cause) {
    endSession();
    throw new SessionEndingError(cause);
  }
  if (tokenSubject() !== userId) throw changedHands();
  if (scopeRootOfToken() !== movedTo) throw movedByAnotherTab();
}

const changedHands = (): SwitchEntityError =>
  new SwitchEntityError('set-context', new Error('the session changed hands during the switch'));

const movedByAnotherTab = (): SwitchEntityError =>
  new SwitchEntityError(
    'scope-check',
    new Error('the token was re-anchored by another tab during the switch'),
  );

/** A 4xx from the write: user-service refused it, so nothing was written. */
const isRefusal = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
};
